import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, Download, Eye, PenTool, Clock, CheckCircle,
  Search, X, ArrowLeft, Loader2
} from 'lucide-react';

// Import document service
import {
  useDocuments,
  useDownloadDocument,
  type Document,
  type DocumentStatus,
} from '../services/documents';

export function ClientDocuments() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);

  // Use document service hook
  const statusFilterValue = statusFilter === 'all' ? undefined : statusFilter as DocumentStatus;
  const { data: documents = [], isLoading } = useDocuments({
    status: statusFilterValue,
    search: searchQuery || undefined,
  });

  // Download mutation
  const downloadMutation = useDownloadDocument();

  const getStatusConfig = (status: Document['status']) => {
    const configs = {
      draft: { label: 'In Progress', color: 'text-yellow-600', bg: 'bg-yellow-100', icon: Clock },
      ready: { label: 'Ready for Review', color: 'text-blue-600', bg: 'bg-blue-100', icon: Eye },
      signature_required: { label: 'Signature Required', color: 'text-orange-600', bg: 'bg-orange-100', icon: PenTool },
      signed: { label: 'Signed', color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle },
      complete: { label: 'Complete', color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle },
    };
    return configs[status];
  };

  // Use all documents query for counts (without filters)
  const { data: allDocuments = [] } = useDocuments();

  // Documents are already filtered by the service, so use directly
  const filteredDocuments = documents;

  const statusCounts = {
    all: allDocuments.length,
    draft: allDocuments.filter(d => d.status === 'draft').length,
    ready: allDocuments.filter(d => d.status === 'ready').length,
    signature_required: allDocuments.filter(d => d.status === 'signature_required').length,
    signed: allDocuments.filter(d => d.status === 'signed').length,
    complete: allDocuments.filter(d => d.status === 'complete').length,
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <a href="/" className="p-2 hover:bg-slate-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </a>
            <div>
              <h1 className="text-xl font-semibold text-slate-900">My Documents</h1>
              <p className="text-sm text-slate-500">
                {isLoading ? 'Loading...' : `${allDocuments.length} documents`}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Status ({statusCounts.all})</option>
              <option value="signature_required">Needs Signature ({statusCounts.signature_required})</option>
              <option value="ready">Ready ({statusCounts.ready})</option>
              <option value="draft">In Progress ({statusCounts.draft})</option>
              <option value="signed">Signed ({statusCounts.signed})</option>
              <option value="complete">Complete ({statusCounts.complete})</option>
            </select>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'signature_required', label: 'Action Required' },
            { key: 'ready', label: 'Ready' },
            { key: 'draft', label: 'In Progress' },
            { key: 'complete', label: 'Completed' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                statusFilter === tab.key
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              {tab.label}
              <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                statusFilter === tab.key ? 'bg-primary-200' : 'bg-slate-100'
              }`}>
                {statusCounts[tab.key as keyof typeof statusCounts]}
              </span>
            </button>
          ))}
        </div>

        {/* Documents List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {filteredDocuments.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-900 mb-1">No documents found</h3>
              <p className="text-sm text-slate-500">
                {searchQuery || statusFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Documents will appear here once created'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredDocuments.map((doc, idx) => {
                const config = getStatusConfig(doc.status);
                const Icon = config.icon;

                return (
                  <motion.div
                    key={doc.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedDoc(doc)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${config.color}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900 truncate">{doc.name}</p>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.color}`}>
                            {config.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                          <span>{doc.matterNumber}</span>
                          <span>•</span>
                          <span>{doc.matterName}</span>
                        </div>
                      </div>

                      <div className="text-right text-sm text-slate-500 flex-shrink-0">
                        <p>Updated {formatDate(doc.updatedAt)}</p>
                      </div>

                      <div className="flex gap-2 flex-shrink-0">
                        {doc.status === 'signature_required' && (
                          <a
                            href={doc.signatureUrl}
                            onClick={(e) => e.stopPropagation()}
                            className="px-3 py-1.5 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 flex items-center gap-1"
                          >
                            <PenTool className="w-3.5 h-3.5" />
                            Sign
                          </a>
                        )}
                        {doc.downloadUrl && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadMutation.mutate(doc.id);
                            }}
                            disabled={downloadMutation.isPending}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50"
                          >
                            {downloadMutation.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Document Detail Modal */}
      {selectedDoc && (
        <DocumentDetailModal
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
          getStatusConfig={getStatusConfig}
        />
      )}
    </div>
  );
}

interface DocumentDetailModalProps {
  document: Document;
  onClose: () => void;
  getStatusConfig: (status: Document['status']) => { label: string; color: string; bg: string; icon: any };
}

function DocumentDetailModal({ document, onClose, getStatusConfig }: DocumentDetailModalProps) {
  const config = getStatusConfig(document.status);
  const Icon = config.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg ${config.bg} flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${config.color}`} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{document.name}</h2>
                <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${config.bg} ${config.color}`}>
                  {config.label}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Document Info */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3">Document Information</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Template</span>
                <span className="text-slate-900">{document.templateName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Matter</span>
                <span className="text-slate-900">{document.matterName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Matter Number</span>
                <span className="text-slate-900">{document.matterNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Created</span>
                <span className="text-slate-900">{formatDate(document.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Last Updated</span>
                <span className="text-slate-900">{formatDate(document.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Signature Status */}
          {document.signers && document.signers.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-3">Signatures</h3>
              <div className="space-y-2">
                {document.signers.map((signer, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{signer.name}</p>
                      <p className="text-xs text-slate-500">{signer.email}</p>
                    </div>
                    {signer.signed ? (
                      <div className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Signed
                      </div>
                    ) : (
                      <span className="text-orange-600 text-sm">Pending</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {document.status === 'signature_required' && (
              <a
                href={document.signatureUrl}
                className="flex-1 py-2.5 bg-orange-600 text-white text-center rounded-lg hover:bg-orange-700 font-medium"
              >
                Sign Document
              </a>
            )}
            {document.downloadUrl && (
              <button className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium flex items-center justify-center gap-2">
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function formatDate(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}
